import type RpcResponse       from '@/core/services/Rpc/RpcResponse';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import type Order             from '@/domains/Orders/Order';
import type OrderPosition     from '@/domains/Orders/OrderPosition';
import type PreorderPosition  from '@/domains/Preorders/PreorderPosition';
import type Person            from '@/domains/Persons/Person';
import type DateRange         from "@/core/types/DateRange";
import RpcService             from '@/core/services/Rpc/RpcService';

/**
 * DAO домена заказов.
 *
 * Бэк отдаёт и заказы, и позиции под одним namespace orders.* (один
 * OrdersController), поэтому DAO заказов и позиций объединены в один класс.
 * Методы — тонкие проброски в RPC: без гидрации, без бизнес-логики.
 */
class OrdersDAO {

	// ======= Заказы =======

	static async getOrder(id: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.getOrder', { id });
	}

	static async getOrdersList(
		filters?:    {
      idPattern?:             number;
      isOfferBased?:          boolean;
      isSamples?:             boolean;
      orderCategoryId?:       string;
      deliveryTypeId?:        string;
      sellerId?:              number;
      sellerCompanyId?:       number;
      buyerId?:               number;
      buyerCompanyId?:        number;
      responsibleId?:         number;
      filterId?:              number;
      categoryId?:            number;
      codePattern?:           string;
      isDraft?:               boolean;
      plannedShipmentPeriod?: { dateFrom: unknown; dateTo: unknown };
      desiredReceiptPeriod?:  { dateFrom: unknown; dateTo: unknown };
      createdPeriod?:         { dateFrom: unknown; dateTo: unknown };
      woPaymentRequests?:     boolean;
      responsibleBuyerId?:    number;
      responsibleSellerId?:   number;
      isForTestsOnly?:        boolean;
      excludePartnersIds?:    number[];
      requireNestedObjects?:  boolean;
      product?:              { id: number }
    },
		pagination?: RpcDataPagination,
		sort?:       RpcDataSort,
	): Promise<RpcResponse> {
		return await RpcService.makeRequest(
			'orders.getOrdersList',
      {
        ...filters,
        /** Если isDraft не передан — по умолчанию черновики не ищем */
        isDraft: filters?.isDraft ?? false,
        product: filters?.product ? { id: filters?.product.id } : undefined,
      },
			pagination,
			sort,
			true, null, null, false,
			{ abortPrevious: true },
		);
	}

	static async add(order: Order): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.addOrder', { order });
	}

	static async update(order: Order, reasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.updateOrder', { order, reasonText });
	}

	static async deleteOrder(id: number, reasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.deleteOrder', {
			order: { id },
			reasonText,
		});
	}

	static async confirm(id: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.confirmOrder', {
			order: { id },
		});
	}

	static async unconfirm(positionId: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.unconfirmOrder', {
			position: { id: positionId },
		});
	}

	static async getCounts(filters?: {
		responsible?:    { id: number };
		seller?:         { id: number };
		deliveryType?:   { id: number };
		orderCategory?:  { id: number };
		isOfferBased?:   boolean;
		isSamples?:      boolean;
		isForTestsOnly?: boolean;
		deliveryStatus?: string | null;
		paymentStatus?:  string | null;
	}): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.getCounts', filters);
	}

	static async sendNotification(
		order:            Order | undefined,
		preorderPosition: PreorderPosition | undefined,
		receivers:        Person[],
		templateData?:    any,
		downloadOnly:     boolean = false,
    sendToSellerAndDownload : boolean = false
	): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.sendNotification', {
			order:            order ? { id: order.id } : undefined,
			preorderPosition: preorderPosition ? { id: preorderPosition.id } : undefined,
			receivers:        receivers.map(person => ({ id: person.id })),
			templateData,
			downloadOnly,
      sendToSellerAndDownload
		});
	}

	// ======= Позиции заказа =======
	// Тот же namespace orders.* — отдельного контроллера позиций на беке нет.

	static async getPosition(id: number, isPreorder?: boolean, isAdminMode?: boolean): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.getPosition', {
			id,
			isPreorder,
			isAdminMode,
		});
	}

	static async getPositionsList(
		filters?:    {
      idPattern?:              number;
      isPreorder?:             boolean;
      isOfferBased?:           boolean;
      isSamples?:              boolean;
      isForTestsOnly?:         boolean;
      orderCategory?:          { id: string };
      deliveryType?:           { id: string };
      seller?:                 { id: number };
      sellerCompany?:          { id: number };
      buyer?:                  { id: number };
      buyerCompany?:           { id: number };
      buyerProductionSite?:    { id: number };
      responsible?:            { id: number };
      product?:                { id: number };
      productCategory?:        { id: number };
      codePattern?:            string;
      isInWork?:               boolean;
      isFinished?:             boolean;
      isDeleted?:              boolean;
      isReceived?:             boolean;
      isShipped?:              boolean;
      desiredReceiptPeriod?:   DateRange;
      plannedShipmentPeriod?:  DateRange;
      plannedPaymentPeriod?:   DateRange;
      createdPeriod?:          DateRange;
      preorderShipmentPeriod?: DateRange;
      shipmentDocumentNumber?: string;
      deliveryStatus?:         string;
      paymentStatus?:          string;
      isAdminMode?:            boolean;
      requireOffers?:          boolean;
      codes?:                  number[];
      isDraft?:                boolean;
    },
		pagination?: RpcDataPagination,
		sort?:       RpcDataSort,
	): Promise<RpcResponse> {
		let isDraft: boolean | undefined;

		if (filters?.isPreorder === true) {
			/** У предзаказов флага isDraft нет — в пэйлоад не попадает (undefined отбрасывается при сериализации) */
			isDraft = undefined;
		}
		else {
			/** Если isDraft не передан — по умолчанию черновики не ищем */
			isDraft = filters?.isDraft ?? false;
		}

		return await RpcService.makeRequest(
			'orders.getPositionsList',
      {
        ...filters,
        isDraft,
        orderCategory:        filters?.orderCategory ? { id: filters.orderCategory.id } : undefined,
        deliveryType:           filters?.deliveryType ? { id: filters.deliveryType.id } : undefined,
        seller:                 filters?.seller ? { id: filters.seller.id } : undefined,
        sellerCompany:          filters?.sellerCompany ? { id: filters.sellerCompany.id } : undefined,
        buyer:                  filters?.buyer ? { id: filters.buyer.id } : undefined,
        buyerCompany:           filters?.buyerCompany ? { id: filters.buyerCompany.id } : undefined,
        buyerProductionSite:    filters?.buyerProductionSite ? { id: filters.buyerProductionSite.id } : undefined,
        responsible:            filters?.responsible ? { id: filters.responsible.id } : undefined,
        product:                filters?.product ? { id: filters.product.id } : undefined,
        productCategory:        filters?.productCategory ? { id: filters.productCategory.id } : undefined,
      },
			pagination,
			sort,
			true, null, null, false,
			{ abortPrevious: true },
		);
	}

	static async updatePosition(position: OrderPosition, reasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.updatePosition', {
			position,
			reasonText,
		});
	}

	static async deletePosition(position: OrderPosition | PreorderPosition, reasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.deletePosition', {
			position: { id: position.id, isPreorder: position.isPreorder },
			reasonText,
		});
	}

	static async closePosition(position: OrderPosition | PreorderPosition, reasonText?: string): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.closePosition', {
			position: { id: position.id, isPreorder: position.isPreorder },
			reasonText,
		});
	}

	static async getShipmentDetails(positionId: number): Promise<RpcResponse> {
		return await RpcService.makeRequest('orders.getShipmentDetails', {
			position: { id: positionId },
		});
	}
}

export default OrdersDAO;